import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Plus, Vote } from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { PollCard } from '../components/polls/PollCard';
import { CreatePollDialog } from '../components/polls/CreatePollDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const FILTERS = ['open', 'closed', 'all'];

export default function PollsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('open');
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchPolls = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/polls`, { params: { status: filter } });
      setPolls(res.data || []);
    } catch (e) {
      console.error('polls: failed to load', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchPolls();
    const id = setInterval(fetchPolls, 15000);
    return () => clearInterval(id);
  }, [fetchPolls]);

  const replace = (p) => setPolls((ps) => ps.map((x) => (x.id === p.id ? p : x)));

  return (
    <Layout>
      <div data-testid="polls-page" className="max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Vote className="w-7 h-7 text-[#D93025]" />{t('polls.title')}
            </h1>
            <p className="text-zinc-600 mt-1 text-sm sm:text-base">{t('polls.subtitle')}</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="polls-new-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-5 self-start sm:self-auto">
            <Plus className="w-4 h-4 mr-2" />{t('polls.newPoll')}
          </Button>
        </div>

        <div className="inline-flex gap-1 p-1 bg-zinc-100 rounded-xl mb-6" data-testid="polls-filter">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`polls-filter-${f}`}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${filter === f ? 'bg-white shadow text-zinc-900' : 'text-zinc-500 hover:text-zinc-800'}`}>
              {t(`polls.filter_${f}`)}
            </button>
          ))}
        </div>

        {!loading && polls.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 border-2 border-dashed border-zinc-200 rounded-2xl" data-testid="polls-empty">
            {t('polls.empty')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="polls-list">
            {polls.map((p) => (
              <PollCard key={p.id} poll={p} onChange={replace} onDeleted={(id) => setPolls((ps) => ps.filter((x) => x.id !== id))} />
            ))}
          </div>
        )}
      </div>
      <CreatePollDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={() => { setFilter('open'); fetchPolls(); }} />
    </Layout>
  );
}
